/**
 * Shared helpers for reading/writing an `answers` map at a flat or nested path, plus a
 * recursive client-only-field stripper. Used by both `usePublicForm` (standalone /
 * /agent dynamic form) and `useFormFlow` (multi-step form flow) so upload behaviour
 * is consistent across entry points.
 */

export const NESTED_FIELD_KEY = /^([^[]+)\[(\d+)\]\.(.+)$/;

/**
 * Parse a flat or nested fieldKey and apply `transformer(currentList)` at that path
 * inside `answers`. Returns a new `answers` object — does not mutate input.
 * `transformer` receives the current list (or an empty array) and must return the
 * new list.
 */
export function setAnswerAtPath(answers, fieldKey, transformer) {
  const m = NESTED_FIELD_KEY.exec(fieldKey);
  if (m) {
    const [, section, rowIdxStr, child] = m;
    const rowIdx = Number(rowIdxStr);
    const rows = Array.isArray(answers[section]) ? [...answers[section]] : [];
    while (rows.length <= rowIdx) rows.push({});
    const row = { ...(rows[rowIdx] || {}) };
    const currentList = Array.isArray(row[child]) ? row[child] : [];
    row[child] = transformer(currentList);
    rows[rowIdx] = row;
    return { ...answers, [section]: rows };
  }
  const currentList = Array.isArray(answers[fieldKey]) ? answers[fieldKey] : [];
  return { ...answers, [fieldKey]: transformer(currentList) };
}

/**
 * Recursively strip client-only fields (leading underscore, e.g. `_file`) from any
 * descriptor objects inside `value`. Called before saveDraft / submit / localStorage
 * writes so the raw File blob held for Retry never gets serialized.
 */
export function stripClientOnlyFields(value) {
  if (Array.isArray(value)) {
    return value.map(stripClientOnlyFields);
  }
  if (value && typeof value === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      if (k.startsWith('_')) continue;
      out[k] = stripClientOnlyFields(v);
    }
    return out;
  }
  return value;
}
